import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Operation, deleteOperation, duplicateOperation } from '../networkRequest/operationsService';
import { useUser } from './useUser';
import { useAgency } from './useAgency';
import { PAGE_PATHS } from '../constants';

export interface UseOperationActionsOptions {
  onError: (error: string) => void;
  onOperationsChange?: (operations: Operation[]) => void;
  allOperations?: Operation[];
  onOperationChange?: (operation: Operation) => void;
}

export const useOperationActions = ({
  onError,
  onOperationsChange,
  allOperations,
  onOperationChange
}: UseOperationActionsOptions) => {
  const { user } = useUser();
  const { agency } = useAgency();
  const navigate = useNavigate();

  const [duplicating, setDuplicating] = useState(false);
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [operationToArchive, setOperationToArchive] = useState<string>('');
  const [operationToArchiveId, setOperationToArchiveId] = useState<string>('');

  const handleDuplicate = async (operation: Operation) => {
    if (!user?.accessToken || !agency?.id) {
      onError('Authentication required');
      return;
    }

    setDuplicating(true);

    try {
      const { data, error } = await duplicateOperation(user.accessToken, operation.id);

      if (data) {
        if (onOperationsChange && allOperations) {
          onOperationsChange([data, ...allOperations]);
          return;
        }
        // default to navigating to the new operation
        navigate(`${PAGE_PATHS.operations}/${data.id}`);
      }

      if (error) {
        onError(error);
      }
    } catch (err) {
      onError('Failed to duplicate operation');
    } finally {
      setDuplicating(false);
    }
  };

  const handleArchiveClick = (operation: Operation) => {
    setOperationToArchive(operation.title);
    setOperationToArchiveId(operation.id);
    setConfirmArchiveOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!operationToArchiveId || !user?.accessToken) return;

    try {
      const { error } = await deleteOperation(user.accessToken, operationToArchiveId);

      if (error) {
        onError(error);
      } else {
        // For list view: update the operations list
        if (onOperationsChange && allOperations) {
          const updatedOperations = allOperations.map(op =>
            op.id === operationToArchiveId
              ? { ...op, status: 'archived' as const }
              : op
          );
          onOperationsChange(updatedOperations);
        }

        // For single operation view: update the operation status to archived
        if (onOperationChange) {
          // Find the current operation and update its status
          const currentOperation = allOperations?.find(op => op.id === operationToArchiveId);
          if (currentOperation) {
            const updatedOperation = { ...currentOperation, status: 'archived' as const };
            onOperationChange(updatedOperation);
          }
        }
      }
    } catch (err) {
      onError('Failed to archive operation');
    } finally {
      setConfirmArchiveOpen(false);
      setOperationToArchive('');
      setOperationToArchiveId('');
    }
  };

  const handleArchiveCancel = () => {
    setConfirmArchiveOpen(false);
    setOperationToArchive('');
    setOperationToArchiveId('');
  };

  return {
    duplicating,
    confirmArchiveOpen,
    operationToArchive,
    handleDuplicate,
    handleArchiveClick,
    handleArchiveConfirm,
    handleArchiveCancel
  };
};